import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';
import { QRCodeSVG } from "qrcode.react";
import { AccountBranding, getStoredLogo, getProtocol } from '../../utils/sqrone-api';



interface BrandImageProps {
    newCollectionDomain: string;
    accountBrandImage: AccountBranding | null
    onImageAdded: (file: File | null) => void;
}

export function BrandImage({ newCollectionDomain, accountBrandImage, onImageAdded }: BrandImageProps) {
  const { token, isAuthenticated } = useAuth();
  const [brandImage, setBrandImage] = useState<string | null>(null);
  const [brandImageFile, setBrandImageFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!accountBrandImage?.logoFileName) return;
  
    const imageUrl = getStoredLogo(accountBrandImage.logoFileName);
  
    setBrandImage(imageUrl);
    setBrandImageFile(null); // this image came from server, not local upload
  }, [accountBrandImage]);

  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/svg+xml')) {
      // Store the actual File object
      setBrandImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setBrandImage(event.target.result as string);
          toast.success('Image uploaded successfully!');
          // Notify parent with the File object
          onImageAdded(file);
        }
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Please upload a JPG, PNG, or SVG image');
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/svg+xml')) {
      // Store the actual File object
      setBrandImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setBrandImage(event.target.result as string);
          toast.success('Image uploaded successfully!');
          // Notify parent with the File object
          onImageAdded(file);
        }
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Please upload a JPG, PNG, or SVG image');
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const removeBrandImage = () => {
    setBrandImage(null);
    setBrandImageFile(null);
    // Notify parent that image was removed
    onImageAdded(null);
    toast.success('Image removed');
  };

  if (!isAuthenticated || !token) {
    return (
    <div className="campaigns-container">
        <div className="no-selection">
        <p>Please sign in to manage campaigns</p>
        </div>
    </div>
    );
 }

  return (
    <div>

            {/* Image Upload Section */}
            <div className="space-y-2">
                <Label>Brand Image for QR Codes (optional)</Label>
                <div
                  onDrop={handleImageDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {!brandImage ? (
                    <div className="space-y-3">
                      <div className="flex justify-center">
                        <div className="p-3 bg-gray-100 rounded-full">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Drag and drop an image here, or click to browse
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Supports JPG, PNG, or SVG (max 5MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/svg+xml"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="image-upload"
                      />
                      <label htmlFor="image-upload">
                        <Button type="button" variant="outline" className="cursor-pointer" asChild>
                          <span>Browse Files</span>
                        </Button>
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="relative inline-block">
                        <img
                          src={brandImage}
                          alt="Brand preview"
                          className="w-24 h-24 object-contain rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={removeBrandImage}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600">Image uploaded successfully</p>
                    </div>
                  )}
                </div>
              </div>

              {/* QR Code Preview */}
              {(brandImage || newCollectionDomain) && (
                <div className="space-y-2">
                  <Label>QR Code Preview</Label>
                  <div className="flex justify-center p-6 bg-gray-50 rounded-lg">
                    <div className="relative inline-block">
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <QRCodeSVG
                          value={newCollectionDomain ? `${getProtocol(newCollectionDomain)}${newCollectionDomain}` : 'https://example.com'}
                          size={200}
                          level="H"
                        />
                      </div>
                      {brandImage && (
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <div className="bg-white p-2 rounded-lg shadow-md">
                            <img
                              src={brandImage}
                              alt="Brand"
                              className="w-12 h-12 object-contain"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    This is how QR codes will look for links in this collection
                  </p>
                </div>
              )}

    </div>
  );
}